import { registerPlugin, WebPlugin } from '@capacitor/core';

import { browserGeraeteKennung } from './browserGeraet';
import { desktopBruecke, istDesktop } from './gleis';
import { liesBase64, schreibeText } from './dateien';
import {
  ausBase64,
  base64,
  hex,
  hollSchluessel,
  indexedDbAblage,
  oeffentlicherTeil,
  SIGNIER_ART,
  type Schluesselablage,
} from './webcrypto';

import { toRelayDateTime, utcOffset } from '../persistence/relay';
import { letzteZeitpruefung } from './clockCheck';
import {
  canonicalManifest,
  signatureFile,
  sigPfad,
  type Manifest,
} from './signature';

/**
 * Dateien beglaubigen.
 *
 * Nativ dahinter: `ios/App/App/SigningPlugin.swift` mit einem P-256-Schlüssel in der Secure
 * Enclave. Hier steht nur, **was** beglaubigt wird und **wo** die Beglaubigung landet — die
 * Aufteilung wie bei allen anderen Plugins.
 */

export interface Identity {
  deviceId: string;
  publicKey: string;
  /** SHA-256 des öffentlichen Schlüssels, hex — damit findet der Server den hinterlegten. */
  keyId: string;
  keyKind: Manifest['keyKind'];
  deviceName: string;
  deviceModel: string;
  osVersion: string;
  vendorId: string;
  /**
   * Ob dieses Gerät eine von Apple prüfbare Bescheinigung ausstellen könnte, dass die
   * Signatur aus einer echten, unveränderten Fassung **dieser** App stammt. Noch nicht
   * genutzt — siehe `docs/beglaubigung.md`, „Wer hat signiert".
   */
  appAttestSupported: boolean;
}

export interface SigningPlugin {
  getIdentity(): Promise<Identity>;
  sha256(options: { path: string }): Promise<{ sha256: string }>;
  sign(options: { payload: string }): Promise<{ signature: string; keyKind: Manifest['keyKind'] }>;
  /**
   * Geheimnisse der App im Schlüsselbund — Serverpasswort, Sitzungstoken.
   *
   * Sie liegen hier und **nicht** in `Preferences`: das ist eine unverschlüsselte plist im
   * Dateisystem der App. Der Weg über dieses Plugin spart ein zweites natives Bauteil; der
   * Schlüsselbundzugriff steht dort ohnehin.
   */
  setSecret(options: { key: string; value: string }): Promise<{ ok: boolean }>;
  getSecret(options: { key: string }): Promise<{ value: string | null }>;
  deleteSecret(options: { key: string }): Promise<{ ok: boolean }>;
  /**
   * Adresse, Anmeldename und Passwort aus der **iOS-Einstellungen-App** (`Settings.bundle`).
   * `available: false` heisst: hier gibt es keine — dann gelten die Felder in der App.
   */
  getManagedSettings(): Promise<ManagedSettings>;
  /** Meldet Fassung und Baudatum an die iOS-Einstellungen (`Root.plist`, Gruppe „Über"). */
  setAppInfo(options: { version: string; build: string }): Promise<void>;
}

/**
 * Was in der iOS-Einstellungen-App steht. `modus` ist leer, solange dort nichts gewählt wurde —
 * iOS legt die `DefaultValue` aus der `Root.plist` erst an, wenn jemand die Seite einmal
 * geöffnet hat. Leer heisst deshalb „keine Angabe", nicht „Relay".
 */
export interface ManagedSettings {
  url: string;
  login: string;
  password: string;
  /** `'relay'`, `'classic'`, ein alter Wert (`relay`, `METIS`, `classic`) oder leer. */
  modus: string;
  available: boolean;
  /*
   * ⚠️ **Die zwei Schalter der Fotos-Kopie stehen hier nicht mehr** (2026-09-15). Dieser Weg
   * konnte nur die iPad-Einstellungen lesen; die Kopie war damit die einzige Stelle der App,
   * an der ein gescannter QR-Code nichts ausrichtete. Sie laufen jetzt über
   * `einstellungen.ts` und seine Rangfolge (QR → MDM → iPad → App → Vorgabe).
   */
}

/** Was `SigningWeb` von aussen bekommt — alles einschiebbar, damit es sich prüfen lässt. */
export interface SigningWebDeps {
  subtle?: SubtleCrypto;
  ablage?: Schluesselablage;
  /** Wie eine Datei unterhalb von Documents als Base64 gelesen wird. */
  liesDatei?: (pfad: string) => Promise<string>;
  /** Wie sich der Browser nennt — im Test fest, sonst aus `navigator`. */
  geraet?: () => { deviceName: string; deviceModel: string; osVersion: string };
}

/**
 * Wie sich der Browser im Manifest nennt.
 *
 * ⚠️ **Kein UA-Zerleger.** Was hier steht, ist eine Ahnung und kein Beleg; eine Zeichenkette
 * aus `navigator.userAgent` auseinanderzunehmen, hiesse eine Genauigkeit vorzutäuschen, die
 * das Feld nicht hat. `userAgentData` gibt es strukturiert — fehlt es, bleibt es bei „Browser".
 */
function browserAngaben(): { deviceName: string; deviceModel: string; osVersion: string } {
  const daten = (navigator as Navigator & {
    userAgentData?: { brands?: { brand: string; version: string }[]; platform?: string };
  }).userAgentData;
  const marke = daten?.brands?.find((b) => !/Not.?A.?Brand/i.test(b.brand));
  return {
    deviceName: 'Browser',
    deviceModel: marke ? marke.brand + ' ' + marke.version : 'Browser',
    osVersion: daten?.platform ?? '',
  };
}

/**
 * Im Browser wird mit **WebCrypto** beglaubigt.
 *
 * ⚠️ **Bis zum 2026-09-22 warf diese Klasse.** Die Begründung war, eine Beglaubigung, die
 * nichts beweist, sei schlimmer als keine — man sähe ihr nicht an, dass sie wertlos ist.
 * Genau dafür gibt es aber `keyKind` im Manifest: die Datei **sagt**, was ihre Unterschrift
 * trägt (`signature.ts`). Und sie trägt mehr als nichts — ECDSA P-256 mit einem privaten
 * Schlüssel, der aus der Seite heraus nicht auslesbar ist.
 *
 * ⚠️ **Was sie nicht trägt:** Hardware und Apples Bescheinigung. Der Schlüssel liegt im
 * Browserprofil auf der Platte. Deshalb `web-crypto` und nicht `keychain` — und deshalb
 * gehört eine Prüfung im Browser nicht in einen Prüfungsraum.
 */
export class SigningWeb extends WebPlugin implements SigningPlugin {
  private readonly subtle: SubtleCrypto;
  private readonly ablage: Schluesselablage;
  private readonly liesDatei: (pfad: string) => Promise<string>;
  private readonly geraet: () => { deviceName: string; deviceModel: string; osVersion: string };
  private paar: Promise<CryptoKeyPair> | null = null;

  constructor(deps: SigningWebDeps = {}) {
    super();
    this.subtle = deps.subtle ?? crypto.subtle;
    this.ablage = deps.ablage ?? indexedDbAblage();
    this.liesDatei =
      deps.liesDatei ??
      ((pfad) => liesBase64(pfad));
    this.geraet = deps.geraet ?? browserAngaben;
  }

  /** Ein Schlüssel je Sitzung, auch bei gleichzeitigen Aufrufen — wie `laufendeAnmeldung`. */
  private schluessel(): Promise<CryptoKeyPair> {
    this.paar ??= hollSchluessel(this.subtle, this.ablage);
    return this.paar;
  }

  async getIdentity(): Promise<Identity> {
    const paar = await this.schluessel();
    const { publicKey, keyId } = await oeffentlicherTeil(this.subtle, paar);
    const angaben = this.geraet();
    return {
      deviceId: browserGeraeteKennung(),
      publicKey,
      keyId,
      keyKind: 'web-crypto',
      ...angaben,
      // Der Browser hat keine Anbieterkennung — das Feld bleibt leer, statt etwas zu erfinden.
      vendorId: '',
      appAttestSupported: false,
    };
  }

  /**
   * ⚠️ **Hier wandert die Datei durch den Speicher**, anders als nativ. `SigningPlugin.swift`
   * liest häppchenweise über den Pfad, gerade damit eine Videoaufnahme den Speicher eines
   * älteren iPads nicht sprengt. Im Browser gibt es keinen Pfad, nur den Inhalt. Am
   * Schreibtisch ist das zu verschmerzen; auf ein Gerät gehört dieser Weg nicht.
   */
  async sha256(options: { path: string }): Promise<{ sha256: string }> {
    const bytes = ausBase64(await this.liesDatei(options.path));
    return { sha256: hex(new Uint8Array(await this.subtle.digest('SHA-256', bytes))) };
  }

  async sign(options: { payload: string }): Promise<{
    signature: string;
    keyKind: Manifest['keyKind'];
  }> {
    const paar = await this.schluessel();
    // ⚠️ `subtle.sign` gibt bei ECDSA `r‖s` — genau das IEEE-P1363, das die `.sig`-Datei
    // ankündigt. Java braucht dafür `SHA256withECDSAinP1363Format` (s. `signature.ts`).
    const roh = await this.subtle.sign(
      SIGNIER_ART,
      paar.privateKey,
      new TextEncoder().encode(options.payload),
    );
    return { signature: base64(new Uint8Array(roh)), keyKind: 'web-crypto' };
  }

  /**
   * Geheimnisse im Browser: nur im Speicher dieser Seite, und weg beim Neuladen.
   *
   * Anders als bei der Beglaubigung ist hier **kein** Fehler richtig — sonst liesse sich die
   * Serveranbindung im Browser gar nicht entwickeln. Aber gespeichert wird nichts: ein
   * Passwort in `localStorage` überlebte den Tag im Browserprofil eines Entwicklungsrechners.
   */
  private speicher = new Map<string, string>();

  async setSecret(options: { key: string; value: string }): Promise<{ ok: boolean }> {
    this.speicher.set(options.key, options.value);
    return { ok: true };
  }

  async getSecret(options: { key: string }): Promise<{ value: string | null }> {
    return { value: this.speicher.get(options.key) ?? null };
  }

  async deleteSecret(options: { key: string }): Promise<{ ok: boolean }> {
    this.speicher.delete(options.key);
    return { ok: true };
  }

  async getManagedSettings(): Promise<ManagedSettings> {
    // Der Browser hat keine iOS-Einstellungen; die Oberfläche lässt ihre Felder dann offen.
    return {
      url: '',
      login: '',
      password: '',
      modus: '',
      available: false,
    };
  }

  async setAppInfo(): Promise<void> {
    // Im Browser gibt es keine Einstellungsseite, auf der etwas stehen könnte.
  }
}

/**
 * Am Desktop beglaubigt der **Hauptprozess** — der private Schlüssel erreicht das Fenster nie.
 *
 * ⭐ **Nur zwei Dinge sind anders**, der Rest ist geerbt: woher die Identität kommt und wer
 * unterschreibt. Das Hashen der Datei, die Geheimnisse, die verwalteten Einstellungen bleiben,
 * wie `SigningWeb` sie hat — ein dritter Ablauf daneben wäre eine dritte Stelle, an der sich
 * die Manifestfelder auseinanderentwickeln können.
 *
 * ⛔ **Rückfall auf den Browserweg, wenn `safeStorage` nicht bereitsteht** (Linux ohne
 * Schlüsselbund). Dann trägt die Datei wieder `web-crypto` — und sie sagt es, statt eine
 * Verwahrung zu behaupten, die es nicht gibt.
 *
 * ⚠️ **Die Entscheidung fällt einmal und gilt für beides.** Käme die Identität aus dem
 * Hauptprozess und die Unterschrift aus dem Browser, passte die Signatur nicht zum
 * mitgelieferten öffentlichen Schlüssel — und niemand sähe es der Datei an.
 */
export class SigningDesktop extends SigningWeb {
  private verwahrt: Promise<boolean> | null = null;

  private nutztHauptprozess(): Promise<boolean> {
    this.verwahrt ??= (async () => {
      const bruecke = desktopBruecke();
      if (!bruecke) return false;
      try {
        return await bruecke.schluessel.verfuegbar();
      } catch {
        // Keine Auskunft heisst hier „nein": lieber der bekannte Browserweg als eine
        // Beglaubigung, die von einem Schlüssel spricht, den niemand bestätigt hat.
        return false;
      }
    })();
    return this.verwahrt;
  }

  override async getIdentity(): Promise<Identity> {
    if (!(await this.nutztHauptprozess())) return super.getIdentity();

    const gewoehnlich = await super.getIdentity();
    const { publicKey, keyId } = await desktopBruecke()!.schluessel.identitaet();
    return { ...gewoehnlich, publicKey, keyId, keyKind: 'safe-storage' };
  }

  override async sign(options: { payload: string }): Promise<{
    signature: string;
    keyKind: Manifest['keyKind'];
  }> {
    if (!(await this.nutztHauptprozess())) return super.sign(options);

    const signature = await desktopBruecke()!.schluessel.signiere(options.payload);
    return { signature, keyKind: 'safe-storage' };
  }
}

export const Signing = registerPlugin<SigningPlugin>('Signing', {
  web: () => (istDesktop() ? new SigningDesktop() : new SigningWeb()),
});

/** Was aus dem Bewertungsvorgang ins Manifest gehört. */
export interface SignSubject {
  /**
   * Der in den Einstellungen vergebene Gerätename. Leer heisst: keiner vergeben, dann steht
   * der vom Betriebssystem gemeldete im Manifest.
   */
  deviceName: string;
  examId: string;
  /** Der Ort im Sinne der Prüfung. Leer, wenn die Datei zu keiner Station gehört. */
  stationId: string;
  candidateId: string;
  examinerId: string;
  appVersion: string;
}

/**
 * Woher die Zeit dieser Beglaubigung stammt.
 *
 * Genommen wird die **letzte** erfolgreiche Prüfung, nicht eine neue: ein NTP-Aufruf je
 * Datei kostete bis zu zweieinhalb Sekunden und bräuchte Netz. Wie viel eine Prüfung von
 * vor drei Stunden noch wert ist, entscheidet der Auswertende — dafür steht `ntpCheckedAt`
 * dabei.
 */
function zeitherkunft(): Pick<
  Manifest,
  'timeSource' | 'ntpOffsetSeconds' | 'ntpCheckedAt' | 'ntpServer'
> {
  const geprüft = letzteZeitpruefung();
  if (!geprüft) {
    return { timeSource: 'system', ntpOffsetSeconds: '', ntpCheckedAt: '', ntpServer: '' };
  }
  return {
    timeSource: 'system-ntp-checked',
    // Eine Nachkommastelle genügt: es geht um „liegt die Uhr daneben", nicht um Messtechnik.
    ntpOffsetSeconds: geprüft.offsetSeconds.toFixed(1),
    ntpCheckedAt: geprüft.checkedAt,
    ntpServer: geprüft.server,
  };
}

/**
 * Eine bereits geschriebene Datei beglaubigen.
 *
 * ⚠️ **Erst schreiben, dann beglaubigen** — und der Hash wird über die Datei gebildet, wie
 * sie auf der Platte liegt. Über den Inhalt im Speicher zu hashen wäre falsch: hätte das
 * Schreiben etwas verändert (Kodierung, Zeilenenden), passte die Beglaubigung nicht zur
 * Datei, sondern zu etwas, das es nie gab.
 *
 * ⚠️ Für **SVG** geht die Beglaubigung ins Dokument statt dahinter (s. `signature.ts`);
 * dafür wird es einmal gelesen und neu geschrieben. Bei allen anderen Formaten wird nur
 * angehängt — eine Videoaufnahme wird nicht noch einmal durch den Speicher gezogen.
 *
 * Wirft nicht: eine misslungene Beglaubigung darf die Datei nicht kosten. Sie bleibt dann
 * unbeglaubigt liegen, und der Fehlschlag geht ins Dateilog.
 */
export async function signFile(
  path: string,
  subject: SignSubject,
  log: (text: string, stufe?: 'error') => Promise<void>,
): Promise<boolean> {
  try {
    const identität = await Signing.getIdentity();
    const { sha256 } = await Signing.sha256({ path });

    const jetzt = new Date();
    const manifest: Manifest = {
      sha256,
      fileName: path.slice(path.lastIndexOf('/') + 1),
      createdAt: toRelayDateTime(jetzt),
      createdAtOffset: utcOffset(jetzt),
      ...zeitherkunft(),
      deviceId: identität.deviceId,
      // Der vergebene Name geht vor: er ist der, unter dem das Gerät geführt wird.
      deviceName: subject.deviceName || identität.deviceName,
      deviceModel: identität.deviceModel,
      osVersion: identität.osVersion,
      vendorId: identität.vendorId,
      appVersion: subject.appVersion,
      examId: subject.examId,
      stationId: subject.stationId,
      candidateId: subject.candidateId,
      examinerId: subject.examinerId,
      keyKind: identität.keyKind,
    };

    const { signature } = await Signing.sign({ payload: canonicalManifest(manifest) });

    // Die Begleitdatei liegt neben der Datei und heisst wie sie, mit `.sig` dahinter. Die
    // Datei selbst bleibt **unangetastet** — kein Anhang, kein Sonderweg für SVG, und ein
    // Programm, das sie neu speichert, kann die Beglaubigung nicht versehentlich zerstören.
    await schreibeText(
      sigPfad(path),
      signatureFile(manifest, signature, identität.publicKey, identität.keyId),
    );
    return true;
  } catch (fehler) {
    await log('Beglaubigung fehlgeschlagen für ' + path + ': ' + String(fehler), 'error');
    return false;
  }
}
